import React, { FC, useState, useEffect } from 'react'
import { Box, useTheme, useMediaQuery, IconButton } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { SPACING } from '@/shared/styles/spacing'
import { LAYOUT_CONSTANTS } from '@/shared/constants/layout'
import { PortalSidebar } from './PortalSidebar'
import { localStorageWrapper } from '@/shared/helpers/localStorage'

interface PortalLayoutProps {
  children: React.ReactNode
}

export const PortalLayout: FC<PortalLayoutProps> = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    // ローカルストレージから折りたたみ状態を復元
    const stored = localStorageWrapper.getItem('portalSidebarCollapsed')
    return stored !== null ? stored : false
  })

  useEffect(() => {
    // 折りたたみ状態をローカルストレージに保存
    localStorageWrapper.setItem('portalSidebarCollapsed', sidebarCollapsed)
  }, [sidebarCollapsed])

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: colorTokens.background.page,
      }}
    >
      {/* サイドバー */}
      <PortalSidebar
        open={isMobile ? sidebarOpen : true}
        onClose={() => setSidebarOpen(false)}
        isMobile={isMobile}
        collapsed={!isMobile && sidebarCollapsed}
        onCollapseToggle={handleSidebarCollapse}
      />
      {/* メインコンテンツエリア */}
      <Box
        component="main"
        sx={[
          {
            flexGrow: 1,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            position: 'relative',
          },
          isMobile
            ? {
                width: '100%',
              }
            : {
                width: `calc(100% - ${
                  sidebarCollapsed
                    ? LAYOUT_CONSTANTS.sidebarCollapsedWidth
                    : LAYOUT_CONSTANTS.sidebarWidth
                }px)`,
              },
          isMobile
            ? {
                ml: 0,
              }
            : {
                ml: sidebarCollapsed
                  ? `${LAYOUT_CONSTANTS.sidebarCollapsedWidth}px`
                  : `${LAYOUT_CONSTANTS.sidebarWidth}px`,
              },
        ]}
      >
        {/* モバイル時のメニューボタン */}
        {isMobile && (
          <IconButton
            onClick={handleSidebarToggle}
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: theme.zIndex.fab,
              bgcolor: colorTokens.blue[600],
              color: 'white',
              '&:hover': {
                bgcolor: colorTokens.blue[700],
              },
              boxShadow: colorTokens.shadow.card,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ p: SPACING.portalPage }}>{children}</Box>
      </Box>
    </Box>
  )
}
